/**
 * MarkdownContent — renders markdown using `react-markdown` and maps every
 * element to design-system tokens so agent reports / chat replies look like
 * native Plura content (no `prose` plugin needed).
 *
 * Use anywhere we render LLM output:
 *   - <MarkdownContent>{report.summary}</MarkdownContent>
 *   - <MarkdownContent compact>{message.content}</MarkdownContent>
 *
 * The `compact` variant tightens spacing for chat bubbles where vertical
 * room is precious.
 */
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Children, isValidElement, useMemo, type ReactElement, type ReactNode } from 'react';

/**
 * A custom renderer for a fenced code block whose language matches the map key.
 * Receives the raw block content (everything between the fence markers) and is
 * expected to return a React node. Returning `null` falls back to the default
 * code block rendering.
 */
export type RichBlockRenderer = (raw: string, language: string) => ReactNode;

type MarkdownContentProps = {
  children: string;
  /** Tighter spacing + smaller headings for chat bubbles. */
  compact?: boolean;
  className?: string;
  /**
   * Map of fenced-block language → custom renderer. When the model writes
   * ```chart-line {...json}``` and `richBlocks['chart-line']` is provided, the
   * renderer fires instead of the default `<pre>` styling. Unknown languages
   * fall back to the default code block.
   */
  richBlocks?: Record<string, RichBlockRenderer>;
};

const MarkdownContent = ({ children, compact, className, richBlocks }: MarkdownContentProps) => {
  const components = useMemo<Components>(
    () => ({
      h1: ({ children: c }) => (
        <h1 className={compact ? 'text-h6 text-t-primary mt-4 mb-2 first:mt-0' : 'text-h4 text-t-primary mt-6 mb-3 first:mt-0'}>
          {c}
        </h1>
      ),
      h2: ({ children: c }) => (
        <h2 className={compact ? 'text-sub-title-1 text-t-primary mt-3 mb-2 first:mt-0' : 'text-h5 text-t-primary mt-5 mb-3 first:mt-0'}>
          {c}
        </h2>
      ),
      h3: ({ children: c }) => (
        <h3 className={compact ? 'text-sub-title-2 text-t-primary mt-3 mb-1.5 first:mt-0' : 'text-h6 text-t-primary mt-4 mb-2 first:mt-0'}>
          {c}
        </h3>
      ),
      h4: ({ children: c }) => (
        <h4 className="text-sub-title-2 text-t-primary mt-3 mb-1.5 first:mt-0">
          {c}
        </h4>
      ),
      h5: ({ children: c }) => (
        <h5 className="text-sub-title-2 text-t-primary mt-3 mb-1.5 first:mt-0">
          {c}
        </h5>
      ),
      h6: ({ children: c }) => (
        <h6 className="text-caption uppercase tracking-wide text-t-tertiary mt-3 mb-1 first:mt-0">
          {c}
        </h6>
      ),
      p: ({ children: c }) => (
        <p className={compact ? 'text-body-2 text-t-primary leading-relaxed mb-2 last:mb-0' : 'text-body-2 text-t-primary leading-relaxed mb-3 last:mb-0'}>
          {c}
        </p>
      ),
      strong: ({ children: c }) => (
        <strong className="font-semibold text-t-primary">{c}</strong>
      ),
      em: ({ children: c }) => <em className="italic">{c}</em>,
      a: ({ children: c, href }) => (
        <a
          href={href}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
          className="text-t-primary underline decoration-s-stroke2 hover:decoration-t-primary"
        >
          {c}
        </a>
      ),
      ul: ({ children: c }) => (
        <ul className="list-disc pl-5 mb-3 last:mb-0 flex flex-col gap-1 text-body-2 text-t-primary marker:text-t-tertiary">
          {c}
        </ul>
      ),
      ol: ({ children: c }) => (
        <ol className="list-decimal pl-5 mb-3 last:mb-0 flex flex-col gap-1 text-body-2 text-t-primary marker:text-t-tertiary">
          {c}
        </ol>
      ),
      li: ({ children: c }) => <li className="leading-relaxed">{c}</li>,
      blockquote: ({ children: c }) => (
        <blockquote className="border-l-2 border-s-stroke2 pl-3 my-3 text-body-2 text-t-secondary italic">
          {c}
        </blockquote>
      ),
      hr: () => <hr className="my-4 border-t border-s-subtle" />,
      code: ({ children: c, className: codeClass }) => {
        // Inline vs block: react-markdown gives a `language-*` className for blocks.
        const isBlock = codeClass?.startsWith('language-');
        if (isBlock) {
          return (
            <code className={`block ${codeClass ?? ''}`}>{c}</code>
          );
        }
        return (
          <code className="px-1.5 py-0.5 rounded bg-b-surface1 text-caption font-mono text-t-primary border border-s-subtle">
            {c}
          </code>
        );
      },
      pre: ({ children: c }) => {
        if (richBlocks) {
          const codeEl = Children.toArray(c).find(
            (child): child is ReactElement<{ className?: string; children?: ReactNode }> =>
              isValidElement(child)
          );
          if (codeEl) {
            const langClass =
              (codeEl.props.className ?? '').match(/language-([^\s]+)/)?.[1] ?? null;
            if (langClass && richBlocks[langClass]) {
              const inner = codeEl.props.children;
              const raw =
                typeof inner === 'string'
                  ? inner
                  : Children.toArray(inner)
                      .map((child) => (typeof child === 'string' ? child : ''))
                      .join('');
              return <>{richBlocks[langClass](raw.replace(/\n$/, ''), langClass)}</>;
            }
          }
        }
        return (
          <pre className="bg-b-surface1 border border-s-subtle rounded-2xl p-4 mb-3 last:mb-0 overflow-auto text-caption font-mono text-t-primary leading-relaxed">
            {c}
          </pre>
        );
      },
      table: ({ children: c }) => (
        <div className="overflow-auto mb-3 last:mb-0 rounded-2xl border border-s-subtle">
          <table className="w-full text-body-2 text-t-primary border-collapse">
            {c}
          </table>
        </div>
      ),
      thead: ({ children: c }) => (
        <thead className="bg-b-surface1 text-t-secondary">{c}</thead>
      ),
      tbody: ({ children: c }) => <tbody>{c}</tbody>,
      tr: ({ children: c }) => (
        <tr className="border-t border-s-subtle first:border-t-0">{c}</tr>
      ),
      th: ({ children: c }) => (
        <th className="text-left px-3 py-2 font-semibold text-caption uppercase tracking-wide">
          {c}
        </th>
      ),
      td: ({ children: c }) => <td className="px-3 py-2 align-top">{c}</td>,
    }),
    [compact, richBlocks]
  );

  return (
    <div className={className ?? ''}>
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
